<script lang="ts" setup>
    import { computed, nextTick, onMounted, reactive } from 'vue';
    import * as _ from 'lodash';

    import { IApptTypeProcedureSearchItem, IProviderSearchItem, ICodeSearchItem, entityInfo, getOfficeMdBaseUri } from '../../lib/ChoiceApi';
    import { IServiceLocationSearchItem } from '../../lib/Api/ServiceLocation';
    import { IEntityInfoById } from '../../lib/Models/EntityModel';
    import { IApptTypeEstimationLinesInput, IApptTypeEstimationLinesResponse, IEstimationPartial, IEstimationLine, 
        IEstimationLineResponse, IEstimationLineDeleteInputs, IEstimationLineUpdateInputs, IEstimationDeleteInputs,
        ILastEstimateResponse, IEstimationPatientPayerResponse, IEstimationFutureAppointmentResponse, CompanySettingAllowedMethod,
        IEstimationChargeAllowedInputs, IEstimationChargeAllowedAggregateResponse, IEstimationChargeAllowedFeeScheduleResponse,
        IEstimationEligServiceTypeResponse, IEstimationLastEligCheckResponse, CompanySettingEstimationLastEligCheck, PayerSourceOfPay,
        IEstimationGetEstimateById, IEstimationSaveCalculationInputs, IEstimationHistoryResponse, IEstimationSaveToMmlInputs,
        IEstimationAppointmentInfoResponse, NetworkTypes, IEstimationConvertToHmtlParams, IEstimationChargeAllowedAggregateInputs, IEstimationCopaymentInfo } from '../../lib/Models/PatientEstimatorModel';
    import { costEstimator, addEstimationLinesByApptType, saveEstimation, deleteOperations } from '../../lib/ChoicePatientEstimatorApi';
    import * as DateUtils from '../../lib/DateUtils';
    import { ToMoneyStr, ToPercentStr } from '../../lib/StringFormat';
    import { DateDisplayFormat, ModalSize, ButtonDisplaySize, ButtonDisplayStyle } from "../../Enums/UiEnums";
    import { ModalWindowOpen } from '../../lib/ChoiceUserConfiguration';
    
    import TitledRow from '../../components/TitledRow.vue';
    import ModalDialog from '../../components/ModalDialog.vue';
    import SearchApptTypeProcedure from '../../components/Search/SearchApptTypeProcedure.vue';
    import SearchProvider from '../../components/Search/SearchProvider.vue';
    import SearchServiceLocation from '../../components/Search/SearchServiceLocation.vue';
    import SearchProcedureCode from '../../components/Search/SearchProcedureCode.vue';
    import SearchDiagnosisCode from '../../components/Search/SearchDiagnosisCode.vue';
    import { popAlert, confirmDialog } from '../../lib/Notification';
    import TextInput from "../../components/TextInput.vue";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
    import vSelect from 'vue-select'; 

    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import IntegerInput from '../../components/IntegerInput.vue';
    import CurrencyInput from '../../components/CurrencyInput.vue';
    import { Alignment, CompanySettingParameterNames, DatabaseIdTypes, MessageChannelName, AppScreens, EligibilityCoverage } from '../../Enums/ChoiceEnums';
    import '../../../css/choice.css';
    import { ChoiceApi } from '../../LibMain';
    import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue';

    const formData = reactive<{
            showSaveLinesButton: boolean,
            showAddLineApptType: boolean,
            showAddLineManualEntry: boolean,
            dateOfService?: DateUtils.NoTzDate,
            patientLastEstimate?: ILastEstimateResponse,
            apptTypeCPTCount: number,
            estimationLinesByApptTypeInputs?: IApptTypeEstimationLinesInput,
            estimationSaveCalculationInputs?: IEstimationSaveCalculationInputs,
            estimationLinesByApptType: IApptTypeEstimationLinesResponse[],
            estimationLinesResponseData: IEstimationLineResponse[],
            estimateSavePartialPostData?: IEstimationPartial;
            estimationId: number,
            estimationLineId: number,
            estimationLineSavePostData?: IEstimationLine,
            estimationLineUpdatePostData?: IEstimationLineUpdateInputs,
            estimationLineDeletePostData?: IEstimationLineDeleteInputs,
            estimationDeletePostData?: IEstimationDeleteInputs,
            apptTypeProcedureSearchItem?: IApptTypeProcedureSearchItem,
            componentKey: number,
            displayCodeSearchError: boolean,
            providerSearchItem?: IProviderSearchItem,
            serviceLocationSearchItem?: IServiceLocationSearchItem,
            codeSearchItem?: ICodeSearchItem,
            codeDxSearchItem?: ICodeSearchItem,
            saveBtnLabel: string,
            lineIsLimitedEdit: boolean,
            labelEmpty: string,
            unitValue: number,
            modifierValue: string,
            chargeValue: number,
            allowedValue: number,
            selectedApptType?: string,
            selectedCPT?: string,
            selectedDx?: string,
            patientPayers?: IEstimationPatientPayerResponse[],
            selectedPayer?: IEstimationPatientPayerResponse,
            futureAppointments?: IEstimationFutureAppointmentResponse[],
            selectedFutureAppt?: IEstimationFutureAppointmentResponse,
            showFutureAppt: boolean,
            companySettingAllowed?: string,
            companySettingLastEligCheck?: string,
            companySettingDefaultCoverageCategories: string,
            chargeAllowedAggregate?: IEstimationChargeAllowedAggregateResponse,
            chargeAllowedFeeSchedule?: IEstimationChargeAllowedFeeScheduleResponse,
            chargeAllowedAggregatePostData?: IEstimationChargeAllowedAggregateInputs,
            chargeAllowedPostData?: IEstimationChargeAllowedInputs,
            selectedNetwork: NetworkTypes,
            serviceTypeCodeList?: IEstimationEligServiceTypeResponse[],
            selectedServiceType?: IEstimationEligServiceTypeResponse,
            lastEligCheck?: IEstimationLastEligCheckResponse,
            patientEntity?: object,
            showPatientBenefitManualEntry: boolean,
            estimateInfo?: IEstimationGetEstimateById,
            selectedServiceLocation?: string,
            selectedProviderName?: string,
            response271?: IEstimationGetEstimateById,
            response271CoPayDistinctInNetwork?: IEstimationCopaymentInfo[],
            response271CoPayDistinctOutOfNetwork?: IEstimationCopaymentInfo[],
            showSummary: boolean,
            savedManualEntry: boolean,
            showEstimateHistory: boolean,
            estimateHistory?: IEstimationHistoryResponse[],
            is271Empty: boolean,
            providerEntityInfo?: IEntityInfoById,
            locationEntityInfo?: IEntityInfoById,
            clearEstimateMessage: string,
            messageManualEntry: string,
            pdfReportId?: number,
            printEstimationId?: number,
            printIsEstimateAccepted: boolean,
            estimationSaveToMmlInputs?: IEstimationSaveToMmlInputs,
            apptInfo?: IEstimationAppointmentInfoResponse,
            convertToHtmlParams?: IEstimationConvertToHmtlParams,
            selectedAllowedUsage?: string
            companySettingDaysToAggregate: string,
            selectedDaysToAggregate?: number,
            showAggregateRecordCount?: boolean,
            showPatientSignatureModal?: boolean,
            patientElectronicSignatureText?: string
            addLineMissingRequiredFields?: boolean
        }>({
            showSaveLinesButton: true,
            showAddLineApptType: false,
            showAddLineManualEntry: false,
            dateOfService: DateUtils.getNoTzToday(),
            apptTypeCPTCount: 0,
            estimationLinesByApptType: [],
            estimationLinesResponseData: [],
            estimationId: 0,
            estimationLineId: 0,
            componentKey: 0,  
            displayCodeSearchError: false,
            saveBtnLabel: "Save Line",
            lineIsLimitedEdit: false,
            labelEmpty: "",
            unitValue: 1,
            modifierValue: "",
            chargeValue: 0, 
            allowedValue: 0,
            showFutureAppt: false,
            companySettingAllowed: "0",
            companySettingLastEligCheck: "0",
            companySettingDefaultCoverageCategories: "30",
            selectedNetwork: NetworkTypes.In,
            showPatientBenefitManualEntry: false,
            showSummary: false,
            savedManualEntry: false,
            showEstimateHistory: false,
            is271Empty: false,
            clearEstimateMessage: "Since you have an estimate started, changing the payer/provider/location can impact many of the estimate inputs.  As such, this will delete your current estimate, so you can start over.  OK to proceed?",
            messageManualEntry: "You need to run a new eligibility check or manually enter benefits.",
            pdfReportId: 0,
            printIsEstimateAccepted: false,
            companySettingDaysToAggregate: "365",
            selectedDaysToAggregate: 365,
            showAggregateRecordCount: false,
            showPatientSignatureModal: false,
            addLineMissingRequiredFields: false
        });


    const props = withDefaults(defineProps<{
        patientId: number,
        appointmentId: number,
        estimateId: number
    }>(), {
    });

    onMounted(async () => { 
        //need to await these async calls before proceeding with remaining promises, since we build on these
        await nextTick();
        await LoadPatientEntityId();
        await LoadPatientPayers();
        await LoadServiceTypeList();
        await LoadCompanySettingDefaultCoverageCategories();
        await LoadLastEstimate();

        //default the selected service type to the first one in the list
        const firstCoverageCategory = coverageCategories.value?.split(',')[0];
        formData.selectedServiceType = formData.serviceTypeCodeList?.find(item => item.serviceTypeCode == firstCoverageCategory);

        const cptCountPromise = LoadApptTypeCPTCount();
        const companySettingAllowedPromise = LoadCompanySettingAllowed();
        const companySettingLastEligCheckPromise = LoadCompanySettingLastEligCheck();
        const lastEligCheckPromise = LoadLastEligCheck();
        const apptPromise = LoadAppointmentInfo();
        const lookupPromise = changePayerProviderLocation();
        await Promise.all([cptCountPromise, companySettingAllowedPromise, companySettingLastEligCheckPromise,
            lastEligCheckPromise, apptPromise, lookupPromise]);  
            
        if (props.estimateId > 0) {
            await EditEstimate(props.estimateId);
        }
    });

    const frequencyMapping: PeriodMap = {
        "7": "Day",
        "23": "Calendar Year",
        "26": "Episode",
        "27": "Visit",
        "29": "Remaining"
    };

    interface PeriodMap {
        [key: string]: string;
    }

    const defaultCoverageCategoriesArray = computed(() => {
        return formData.companySettingDefaultCoverageCategories?.split(',').map(item => item.trim());
    });

    const usedCoverageCategoriesArray = computed(() => {        
        return formData.estimateInfo?.multipleCoverageCategoriesUsed?.split(',').map(item => item.trim());
    });    

    const multipleCoverageCategoriesUsedMessage = computed<string|undefined>(() => {  
        let message = undefined;   
        message = usedCoverageCategoriesArray.value?.length ?? 0 > 1 ? `This estimate used multiple coverage categories to determine benefits:  [${usedCoverageCategoriesArray.value}]` : undefined;

        if (!message) {
            message = ((formData.estimateInfo?.estimationId ?? 0) > 0 || defaultCoverageCategoriesArray.value.length === 1 ) ? undefined : `Based on your company setting, new estimates will use these coverage categories:  [${defaultCoverageCategoriesArray.value}]`;
        }

        return message;
    });

    const coverageCategories = computed(() => {        
        let coverageCategories = undefined;    
        coverageCategories = usedCoverageCategoriesArray.value?.length ?? 0 > 1 ? formData.estimateInfo?.multipleCoverageCategoriesUsed : undefined;

        if (!coverageCategories) {
            coverageCategories = formData.companySettingDefaultCoverageCategories;
        }

        return coverageCategories;
    }); 

    const copayDataInNetwork = computed(() => {
        return formData?.response271CoPayDistinctInNetwork?.map(item => {
            // Check if copayAmount is a number, if not, try to parse it; if that fails, default to 0
            const copayAmount = Number(item.copayAmount) || 0;
            
            // Format copayAmount to two decimal places
            const formattedCopayAmount = copayAmount.toFixed(2);

            // Generate the frequency description using the frequencyMapping or default to "Unknown"
            const frequencyDescription = item.copayFrequency && frequencyMapping[item.copayFrequency]
                ? `${formattedCopayAmount} (${frequencyMapping[item.copayFrequency]})` : "Unknown";

            // Return the new object with the formatted copayAmount
            return {
                ...item,
                frequencyDescription: frequencyDescription
            };
        });
    });

    const copayDataOutOfNetwork = computed(() => {
        return formData?.response271CoPayDistinctOutOfNetwork?.map(item => {
            const copayAmount = Number(item.copayAmount) || 0;
            
            const formattedCopayAmount = copayAmount.toFixed(2);

            const frequencyDescription = item.copayFrequency && frequencyMapping[item.copayFrequency]
                ? `${formattedCopayAmount} (${frequencyMapping[item.copayFrequency]})` : "Unknown";

            return {
                ...item,
                frequencyDescription: frequencyDescription
            };
        });
    });

    const copayDataCountInNetwork = computed(() => { return copayDataInNetwork.value?.length ?? 0; });

    const copayDataCountOutOfNetwork = computed(() => { return copayDataOutOfNetwork.value?.length ?? 0; });

    const hasMultipleCopayInNetwork = computed<boolean>(() => {        
        return copayDataCountInNetwork.value > 1;
    });

    const hasMultipleCopayOutOfNetwork = computed<boolean>(() => {        
        return copayDataCountOutOfNetwork.value > 1;
    });

    const copayClass = computed(() => { return (hasMultipleCopayInNetwork.value || hasMultipleCopayOutOfNetwork.value) && !formData.estimateInfo?.isEstimateAccepted ? `red-text` : `text-isalus-primary`});
    const copayStyle = computed(() => { return (hasMultipleCopayInNetwork.value || hasMultipleCopayOutOfNetwork.value) && !formData.estimateInfo?.isEstimateAccepted ? `cursor: pointer;` : ``});
    const copayTitleClass = computed(() => { return (hasMultipleCopayInNetwork.value || hasMultipleCopayOutOfNetwork.value) && !formData.estimateInfo?.isEstimateAccepted ? `There are multiple co-pays returned in the eligibility response. Click here to open Manual Entry to change if desired.` : `Copay Amount`});
    const copayButtonClass = computed(() => { return (hasMultipleCopayInNetwork.value || hasMultipleCopayOutOfNetwork.value) && !formData.estimateInfo?.isEstimateAccepted ? `btn btn-danger` : `btn btn-outline-primary btn-sm`});

    const payerEntityId = computed<number|undefined>(() => {        
        return formData.selectedPayer?.payerEntityId;
    });

    const isSelfPay = computed<boolean>(() => {        
        return PayerSourceOfPay.SelfPay == formData.selectedPayer?.sourceOfPayCode;
    });

    const providerEntityId = computed<number|undefined>(() => {        
        return formData.selectedFutureAppt?.providerEntityId ?? formData.providerSearchItem?.entity_id;
    });

    const locationEntityId = computed<number|undefined>(() => {        
        return formData.selectedFutureAppt?.locationEntityId ?? formData.serviceLocationSearchItem?.entity_id;
    });

    const apptTypeId = computed<number|undefined>(() => {
        return formData.selectedFutureAppt?.appointmentTypeId ?? formData.apptTypeProcedureSearchItem?.appointmentTypeId;
    });

    const estimateStatus = computed<string>(() => {
        return formData.estimateInfo?.isEstimateAccepted ? "Accepted" : "Proposed";
    });

    const errorAggregateCalc = computed<string|undefined>(() => {
        const usingAggregate = ((CompanySettingAllowedMethod.PaymentAggregate == formData.selectedAllowedUsage) && (!formData.selectedDaysToAggregate || 0 == formData.selectedDaysToAggregate || formData.selectedDaysToAggregate > 365));
        return usingAggregate ? "Required to aggregate payments (max 365)" : undefined;
    });

    const totalChargeAmountExtended = computed<number>(() => {        
        return _.sumBy(formData.estimationLinesResponseData, est => { return calcExtendedAmount(est.units, est.chargeAmount); });
    });

    const totalAllowedAmountExtended = computed<number>(() => {        
        return _.sumBy(formData.estimationLinesResponseData, est => { return calcExtendedAmount(est.units, est.allowedAmount); });
    });

    const showAddLineButtons = computed<boolean>(() => {
        return ( !formData.selectedPayer || (!formData.selectedServiceLocation) || (!formData.selectedProviderName) || (!formData.dateOfService) ) ? false : true;
    });

    const summaryPatientCoPay = computed<number|undefined>(() => {        
        return ("in" === formData.selectedNetwork) ? formData.estimateInfo?.copayAmountInNetwork : formData.estimateInfo?.copayAmountOutOfNetwork;
    });

    const summaryDeductRemainingInd = computed<number|undefined>(() => {        
        return ("in" === formData.selectedNetwork) ? formData.estimateInfo?.deductibleAmountRemainingIndInNetwork : formData.estimateInfo?.deductibleAmountRemainingIndOutOfNetwork;
    });

    const summaryDeductRemainingFam = computed<number|undefined>(() => {        
        return ("in" === formData.selectedNetwork) ? formData.estimateInfo?.deductibleAmountRemainingFamInNetwork : formData.estimateInfo?.deductibleAmountRemainingFamOutOfNetwork;
    });

    const summaryDeductInd = computed<number|undefined>(() => {        
        return ("in" === formData.selectedNetwork) ? formData.estimateInfo?.deductibleAmountIndInNetwork : formData.estimateInfo?.deductibleAmountIndOutOfNetwork;
    });

    const summaryDeductFam = computed<number|undefined>(() => {        
        return ("in" === formData.selectedNetwork) ? formData.estimateInfo?.deductibleAmountFamInNetwork : formData.estimateInfo?.deductibleAmountFamOutOfNetwork;
    });

    const summaryCoIns = computed<number|undefined>(() => {        
        return ("in" === formData.selectedNetwork) ? formData.estimateInfo?.coinsurancePctInNetwork : formData.estimateInfo?.coinsurancePctOutOfNetwork;
    });

    const summaryOopRemainingInd = computed<number|undefined>(() => {        
        return ("in" === formData.selectedNetwork) ? formData.estimateInfo?.outOfPocketAmountRemainingIndInNetwork : formData.estimateInfo?.outOfPocketAmountRemainingIndOutOfNetwork;
    });

    const totalEstAllowedOrCharge = computed<number>(() => {  
        //get the total amount that we'll use for the estimate calculation.  If there's not an allowed amount, use the charge amount.  
        return _.sumBy(formData.estimationLinesResponseData, est => { return (est.allowedAmount ?? 0) > 0 ? calcExtendedAmount(est.units, est.allowedAmount) ?? 0 : calcExtendedAmount(est.units, est.chargeAmount) ?? 0 });
    });

    //Estimate Calc's are below
    const calcPatientCoPay = computed<number|undefined>(() => {   
        //if the allowed/charge is less than copay, patient will only pay up to the allowed/charge, or up to Out of Pocket remaining. If no OOP provided, copay still applies.
        //if insured is a Self Pay, it's 100% patient cost
        const oopRem = (summaryOopRemainingInd.value != undefined && summaryOopRemainingInd.value < (summaryPatientCoPay.value ?? 0)) ? summaryOopRemainingInd.value : summaryPatientCoPay.value;
        const copay = ((summaryPatientCoPay.value ?? 0) <= totalEstAllowedOrCharge.value) ? (summaryPatientCoPay.value ?? 0) : totalEstAllowedOrCharge.value;
        const patCoPay = (oopRem != undefined && oopRem < copay) ? oopRem : copay;
        return (isSelfPay.value || EligibilityCoverage.Inactive == formData.lastEligCheck?.overallCoverage ? totalEstAllowedOrCharge.value : patCoPay);     
    });

    const calcPatientDeduct = computed<number|undefined>(() => {   
        const hasRemDeduct = (summaryDeductRemainingInd.value ?? 0) > 0 ? true : false;
        const deductAmount = ((calcPatientCoPay.value ?? 0) + (summaryDeductRemainingInd.value ?? 0)) <= totalEstAllowedOrCharge.value ? summaryDeductRemainingInd.value : calcRunningBalance.value;
        return (hasRemDeduct ? deductAmount : 0);                      
    });

    const calcPatientCoInsurance = computed<number|undefined>(() => {   
        //Co-insurance should be factored in at the percentage of the allowed amount and up to the point where out of pocket is met. If no OOP is provided, co-ins still applies.
        const CoInsCalc = ((totalEstAllowedOrCharge.value * (summaryCoIns.value ?? 0))/100);
        const coInsAmount = (summaryOopRemainingInd.value != undefined && CoInsCalc > summaryOopRemainingInd.value) ? summaryOopRemainingInd.value : CoInsCalc;

        //determine if copay and deductible already covers allowed cost, if so, don't apply co-insurance
        const coPayAndDeductible = (calcPatientCoPay.value ?? 0) + (calcPatientDeduct.value ?? 0);
        const coInsuranceAmt = totalEstAllowedOrCharge.value > coPayAndDeductible ? coInsAmount : 0;

        //Handle scenarios where the balance after copay and deductible is less than the calculated co-insurance amount
        const balanceAfterCoPayAndDeductible = totalEstAllowedOrCharge.value - coPayAndDeductible;
        const coInsRecalc = (balanceAfterCoPayAndDeductible > 0 && balanceAfterCoPayAndDeductible < coInsuranceAmt) ? balanceAfterCoPayAndDeductible : coInsuranceAmt;

        return coInsRecalc;
    });

    const calcTotalPatient = computed<number|undefined>(() => {  
        return ((calcPatientCoPay.value ?? 0) + (calcPatientDeduct.value ?? 0) + (calcPatientCoInsurance.value ?? 0));
    });

    const calcRunningBalance = computed<number>(() => {   
        //keep a running patient balance, let's start it off at the total allowed/charged
        return (totalEstAllowedOrCharge.value - (calcPatientCoPay.value ?? 0));              
    });

    const calcInsuranceCoPay = computed<number>(() => {    
        return (isSelfPay.value || EligibilityCoverage.Inactive == formData.lastEligCheck?.overallCoverage ? 0 : totalEstAllowedOrCharge.value - (calcPatientCoPay.value ?? 0));
    });

    const calcInsuranceDeduct = computed<number>(() => {    
        return (calcInsuranceCoPay.value - (calcPatientDeduct.value ?? 0));
    });

    const calcInsuranceCoInsurance = computed<number>(() => {    
        return (calcInsuranceDeduct.value - (calcPatientCoInsurance.value ?? 0));
    });

    const calcTotalInsurance = computed<number>(() => {    
        return (totalEstAllowedOrCharge.value - (calcTotalPatient.value ?? 0));
    });

    const hasPatientPayers = computed<boolean>(() => {
        return formData.patientPayers?.length != 0 ? true : false;
    });

    const needsNewEligCheck = computed<boolean>(() => {
        if (!formData.lastEligCheck?.lastEligibilityDate) {
            return true;
        }
        
        let newLastEligCheckDate = new Date(formData.lastEligCheck.lastEligibilityDate);

        switch(formData.companySettingLastEligCheck)
            {
                case CompanySettingEstimationLastEligCheck.CurrentDate:
                    if (newLastEligCheckDate < DateUtils.startOfDay() ) {                        
                        return true;
                    }
                    break;

                case CompanySettingEstimationLastEligCheck.Last15Days:
                    if (newLastEligCheckDate < DateUtils.addDay(new Date(), -15)) {
                        return true;
                    }
                    break;
                
                case CompanySettingEstimationLastEligCheck.CurrentMonth:
                    if (newLastEligCheckDate < DateUtils.startOfMonth(new Date())) {
                        return true;
                    }
                    break;

                default: return false;
            }

            return false;
    });
 

    //functions
    async function LoadAppointmentInfo() {
        if (props.appointmentId != 0) {
            const resp = await costEstimator.getAppointmentInfo(props.appointmentId);
            if (!resp) {
                return;
            }
            formData.apptInfo = resp;

            //Now that we have appt data, let's pre-load the provider and location search boxes for the user
            formData.providerSearchItem = {
                entity_id: formData.apptInfo.providerEntityId,
                provider_name: formData.apptInfo.providerName,
                npi: formData.apptInfo.providerNpi,
                group_name: formData.apptInfo.providerGroupName,
                rendering_provider: formData.apptInfo.providerIsRenderingProvider,
                active: formData.apptInfo.providerActive
            }
            formData.selectedProviderName = formData.apptInfo.providerName;

            formData.serviceLocationSearchItem = {
                entity_id: formData.apptInfo.serviceLocationEntityId,
                service_location_name: formData.apptInfo.serviceLocationName,
                address1: formData.apptInfo.serviceLocationAddress,
                city: formData.apptInfo.serviceLocationCity,
                state: formData.apptInfo.serviceLocationState,
                zip_code: formData.apptInfo.serviceLocationZipCode,
                is_billable: formData.apptInfo.serviceLocationBillable
            }
            formData.selectedServiceLocation = formData.apptInfo.serviceLocationName

            await changePayerProviderLocation();
        }        
    }

    async function LoadLastEstimate() {
        const resp = await costEstimator.lastEstimate(props.patientId);
        if (!resp) {
            return;
        }
        formData.patientLastEstimate = resp;
    }

    async function LoadPatientEntityId() {
        let patId = 0;
        if (props.estimateId) {
            const result = await costEstimator.getEstimationById(props.estimateId);
            if (result) {
                patId = result.patientId ?? 0;
            }
        }

        patId = patId > 0 ? patId : props.patientId;

        const resp = await ChoiceApi.patient.entity(patId);
        if (!resp) {
            return;
        }
        formData.patientEntity = resp;
    }

    async function LoadApptTypeCPTCount() {
        const resp = await costEstimator.getApptTypeCPTCount();
        formData.apptTypeCPTCount = resp?.apptTypeCptCount ?? 0;
    }

    async function LoadPatientPayers() {
        const resp = await costEstimator.getPatientPayers(props.patientId);
        formData.patientPayers = resp ?? [];
        formData.selectedPayer = formData.patientPayers?.find(item => item.insuredCoverage == 1);
    }

    async function LoadFutureAppointments() {
        const resp = await costEstimator.getFutureAppointments(props.patientId);
        formData.futureAppointments = resp ?? [];

        //if there's only one future appt, let's default to it
        formData.selectedFutureAppt = (1 === formData.futureAppointments.length) ? formData.futureAppointments[0] : undefined;
    }

    async function LoadCompanySettingAllowed() {
        const resp = await costEstimator.getCompanySetting(CompanySettingParameterNames.PatientCostEstimator, 1);

        if (!resp) {
            formData.companySettingAllowed = CompanySettingAllowedMethod.PaymentAggregate;
        }
        formData.companySettingAllowed = resp.parameterValue;
        formData.selectedAllowedUsage = formData.companySettingAllowed;

        const respDays = await costEstimator.getCompanySetting(CompanySettingParameterNames.PatientCostEstimator, 5);
        if (!respDays) {
            formData.companySettingDaysToAggregate = "365";
        }
        formData.companySettingDaysToAggregate = respDays.parameterValue ?? "365";
        formData.selectedDaysToAggregate = parseInt(formData.companySettingDaysToAggregate, 10);
    }

    async function LoadCompanySettingLastEligCheck() {
        const resp = await costEstimator.getCompanySetting(CompanySettingParameterNames.PatientCostEstimator, 2);
        formData.companySettingLastEligCheck = resp.parameterValue;
    }

    async function LoadCompanySettingDefaultCoverageCategories() {
        const resp = await costEstimator.getCompanySetting(CompanySettingParameterNames.PatientCostEstimator, 9);
        formData.companySettingDefaultCoverageCategories = resp.parameterValue ?? "30";
    }
    
    async function LoadServiceTypeList() {
        //first try to pull specific service type codes from the 271 response for the payer in context, if there is a 271
        const resp = await costEstimator.get271ResponseServiceTypesList(formData.lastEligCheck?.patientTransactionId ?? 0);
        formData.serviceTypeCodeList = resp ?? [];

        //check if specific codes were returned.  If not, return entire service type code list from cw_lookup
        if (0 === formData.serviceTypeCodeList.length) {
            const resp = await costEstimator.getEligServiceTypeList();
            formData.serviceTypeCodeList = resp ?? [];
        }
    }

    async function changePayerProviderLocation() { 
        //since user changed Payer, that affects a lot of things with the estimate, so reset everything by deleting the estimate, this will reset objects
        
        //only need to reset if they have an estimate id in context
        //first confirm that they want to wipe current selections
        if ((formData.estimateInfo?.estimationId ?? 0) > 0) {
            //no changes allowed when estimate is accepted
            if (formData.estimateInfo?.isEstimateAccepted) {
                return;
            }
            const result = await confirmDialog(formData.clearEstimateMessage, { RejectText: "Cancel", ConfirmText: "Confirm" });    
            
            if (result) {  
                DeleteEstimate(formData.estimateInfo?.estimationId ?? 0, true, false);
                formData.response271 = undefined;
                formData.savedManualEntry = false;
            }
            else {
                //put original values back if they do not confirm
                formData.selectedPayer = formData.patientPayers?.find(item => item.payerEntityId == formData.estimateInfo?.payerEntityId);

                //get entity info for the selected Provider and Location so that we can populate the search boxes with original values
                const providerInfo = await entityInfo.getEntityInfoByDatabaseId({ id: formData.estimateInfo?.providerEntityId ?? 0, idType: DatabaseIdTypes.entity_id });
                const locationInfo = await entityInfo.getEntityInfoByDatabaseId({ id: formData.estimateInfo?.serviceLocationEntityId ?? 0, idType: DatabaseIdTypes.entity_id});
                formData.selectedProviderName = providerInfo ? providerInfo.entityName : undefined;
                formData.selectedServiceLocation = locationInfo ? locationInfo.entityName : undefined;
            }
        } 
        
        //if user changes the payer, we need to update the LastEligCheck and force a new Load271Response using the new payer
        //also refresh the service type list based on last elig check for this payer
        await LoadLastEligCheck();
        await LoadServiceTypeList();
    }

    async function LoadLastEligCheck() {
        const resp = await costEstimator.getLastEligCheck(props.patientId, formData.selectedPayer?.payerEntityId ?? 0);
        formData.lastEligCheck = resp;
        await Load271Response();
    }

    async function Load271Response() {
        await updateEstimateWith271Response();      
    }

    async function LoadChargeAllowedAggregate() {
        formData.chargeAllowedAggregatePostData = {
            baseInputs: {
                procedureCode: formData.codeSearchItem?.code ?? "",
                payerEntityId: payerEntityId.value ?? 0,
                providerEntityId: providerEntityId.value ?? 0,
                locationEntityId: locationEntityId.value ?? 0
            },
            daysToAggregate: formData.selectedDaysToAggregate ?? 365
        };

        const resp = await costEstimator.getChargeAllowedByAggregate(formData.chargeAllowedAggregatePostData);
        formData.chargeAllowedAggregate = resp;
    }

    async function LoadChargeAllowedFeeSchedule() {
        formData.chargeAllowedPostData = {
            procedureCode: formData.codeSearchItem?.code ?? "",
            payerEntityId: payerEntityId.value ?? 0,
            providerEntityId: providerEntityId.value ?? 0,
            locationEntityId: locationEntityId.value ?? 0
        }
        const resp = await costEstimator.getChargeAllowedByFeeSchedule(formData.chargeAllowedPostData);
        formData.chargeAllowedFeeSchedule = resp;
    }

    async function LoadEstimateHistory() {
        const resp = await costEstimator.getEstimateHistory(props.patientId);

        if (!resp) {
            return;
        }

        formData.estimateHistory = resp;
    }

    async function GetEstimationLinesByApptType(inputData: IApptTypeEstimationLinesInput) {
        const resp = await addEstimationLinesByApptType.getEstimateLines(inputData)
        formData.estimationLinesByApptType = resp ?? [];           
                
        //save partial to Estimate table
        await saveEstimate();
        
    }

    async function saveEstimate() {
        if (!formData?.estimationLinesByApptType) {
            return;
        }

        //sum up amounts for all lines
        const totalChargeAmount = _.sumBy(formData.estimationLinesByApptType, est => { return calcExtendedAmount(est.units, est.chargeAmount) ?? 0; });
        const totalAllowedAmount = _.sumBy(formData.estimationLinesByApptType, est => { return calcExtendedAmount(est.units, est.allowedAmount) ?? 0; });

        const multipleCategories = coverageCategories.value?.split(',').length > 1;

        formData.estimateSavePartialPostData = {
            patientId: props.patientId,
            appointmentTypeId: apptTypeId.value ?? formData.apptTypeProcedureSearchItem?.appointmentTypeId ?? undefined,
            dateOfService: formData.dateOfService,   
            payerEntityId: payerEntityId.value,
            providerEntityId: providerEntityId.value ?? formData.providerSearchItem?.entity_id,
            serviceLocationEntityId: locationEntityId.value ?? formData.serviceLocationSearchItem?.entity_id, 
            isManualEntry: false,
            isEstimateAccepted: false,
            chargeAmount: totalChargeAmount,
            allowedAmount: totalAllowedAmount,
            firstCoverageCategory: formData.selectedServiceType?.serviceTypeCode,
            multipleCoverageCategoriesUsed:  multipleCategories ? coverageCategories.value : null,
            outputEstimationId: undefined
        }

        const resp = await saveEstimation.saveEstimatePartial(formData.estimateSavePartialPostData);

        if (!resp) {
            popAlert("Error saving estimate lines using Appt Type: ApptTypeId = " + formData.apptTypeProcedureSearchItem?.appointmentTypeId, "Error Saving Estimate Lines");
            return;
        }

        //save the returned identity so I can use to add estimation lines later
        formData.estimateInfo = resp;

        //Now update the input fields for the calc with the 271 response if I have one
        await updateEstimateWith271Response();       

        //now save to EstimateLines table
        await saveEstimateLine();        
    }

    async function saveEstimateLine() {
        if ((undefined === formData.estimateInfo?.estimationId) || (!formData.estimationLinesByApptType)) {
            popAlert("Error saving estimate lines - no estimation Id available.", "Error Saving Estimate Line");
            return;
        }

        //loop over estimation lines collection to save each line to db
        for (const line of formData.estimationLinesByApptType) {
            formData.estimationLineSavePostData = {
                estimationId: formData.estimateInfo?.estimationId ?? 0,
                procedureCode: line.procedureCode ?? "",
                modifier: line.modifiers ?? "",
                diagnosisCode: line.icdCodes ?? "",
                units: formData.unitValue,
                chargeAmount: line.chargeAmount ?? 0,
                allowedAmount: line.allowedAmount ?? 0
            }

            //save the estimation line data.
            const resp = await saveEstimation.saveEstimateLine(formData.estimationLineSavePostData);            
            formData.estimationLinesResponseData = resp ?? [];
        }
    }

    async function assignIfUndefined(target: any, value: any) {
        if (undefined === target || null === target) {
            target = value;
        }
        return target;
    }

    async function updateEstimateWith271Response() {
        //let's loop over the coverage categories set on the company setting
        for (const categoryCode of defaultCoverageCategoriesArray.value) {
            const resp = await costEstimator.get271Response(formData.lastEligCheck?.patientTransactionId ?? 0, categoryCode);

            //do not return the 271 response if it doesn't meet the company setting, i.e., too old
            if (!resp || needsNewEligCheck.value) {
                formData.response271 = undefined;
                return;
            }

            formData.response271 = resp;

            //There can be duplicate copayments on the response, let's get unique values
            const uniqueCopaysMapInNetwork = new Map();
            const uniqueCopaysMapOutOfNetwork = new Map();
            
            //Iterate through the array and add items to the Map
            resp?.copayDataInNetwork?.forEach(item => {
                uniqueCopaysMapInNetwork.set(JSON.stringify(item), item);
            });

            resp?.copayDataOutOfNetwork?.forEach(item => {
                uniqueCopaysMapOutOfNetwork.set(JSON.stringify(item), item);
            });

            // Convert Map values back to an array to get unique items
            const uniqueCopaysArrayInNetwork = Array.from(uniqueCopaysMapInNetwork.values());
            formData.response271CoPayDistinctInNetwork = await assignIfUndefined(formData.response271CoPayDistinctInNetwork, uniqueCopaysArrayInNetwork);

            const uniqueCopaysArrayOutOfNetwork = Array.from(uniqueCopaysMapOutOfNetwork.values());
            formData.response271CoPayDistinctOutOfNetwork = await assignIfUndefined(formData.response271CoPayDistinctOutOfNetwork, uniqueCopaysArrayOutOfNetwork);

            if (formData.estimateInfo && formData.response271) {
                //since we can now be looping over this function by coverage category, we only want to update properties that are not already set/defined
                if (formData.response271?.coinsurancePctInNetwork) {
                    formData.estimateInfo.coinsurancePctInNetwork = await assignIfUndefined(formData.estimateInfo.coinsurancePctInNetwork, formData.response271?.coinsurancePctInNetwork < 1 ? (formData.response271?.coinsurancePctInNetwork) * 100 : formData.response271.coinsurancePctInNetwork);
                }

                if (formData.response271?.coinsurancePctOutOfNetwork) {
                    formData.estimateInfo.coinsurancePctOutOfNetwork = await assignIfUndefined(formData.estimateInfo.coinsurancePctOutOfNetwork, formData.response271?.coinsurancePctOutOfNetwork < 1 ? (formData.response271?.coinsurancePctOutOfNetwork) * 100 : formData.response271.coinsurancePctOutOfNetwork);
                }

                formData.estimateInfo.copayAmountInNetwork = await assignIfUndefined(formData.estimateInfo.copayAmountInNetwork, formData.response271?.copayAmountInNetwork);
                formData.estimateInfo.copayAmountOutOfNetwork = await assignIfUndefined(formData.estimateInfo.copayAmountOutOfNetwork, formData.response271?.copayAmountOutOfNetwork);
                formData.estimateInfo.deductibleAmountFamInNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountFamInNetwork, formData.response271?.deductibleAmountFamInNetwork);
                formData.estimateInfo.deductibleAmountFamOutOfNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountFamOutOfNetwork, formData.response271?.deductibleAmountFamOutOfNetwork);
                formData.estimateInfo.deductibleAmountIndInNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountIndInNetwork, formData.response271?.deductibleAmountIndInNetwork);
                formData.estimateInfo.deductibleAmountIndOutOfNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountIndOutOfNetwork, formData.response271?.deductibleAmountIndOutOfNetwork);
                formData.estimateInfo.deductibleAmountRemainingFamInNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountRemainingFamInNetwork, formData.response271?.deductibleAmountRemainingFamInNetwork);
                formData.estimateInfo.deductibleAmountRemainingFamOutOfNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountRemainingFamOutOfNetwork, formData.response271?.deductibleAmountRemainingFamOutOfNetwork);
                formData.estimateInfo.deductibleAmountRemainingIndInNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountRemainingIndInNetwork, formData.response271?.deductibleAmountRemainingIndInNetwork);
                formData.estimateInfo.deductibleAmountRemainingIndOutOfNetwork = await assignIfUndefined(formData.estimateInfo.deductibleAmountRemainingIndOutOfNetwork, formData.response271?.deductibleAmountRemainingIndOutOfNetwork);
                formData.estimateInfo.outOfPocketAmountFamInNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountFamInNetwork, formData.response271?.outOfPocketAmountFamInNetwork);
                formData.estimateInfo.outOfPocketAmountFamOutOfNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountFamOutOfNetwork, formData.response271?.outOfPocketAmountFamOutOfNetwork);
                formData.estimateInfo.outOfPocketAmountIndInNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountIndInNetwork, formData.response271?.outOfPocketAmountIndInNetwork);
                formData.estimateInfo.outOfPocketAmountIndOutOfNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountIndOutOfNetwork, formData.response271?.outOfPocketAmountIndOutOfNetwork);
                formData.estimateInfo.outOfPocketAmountRemainingFamInNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountRemainingFamInNetwork, formData.response271?.outOfPocketAmountRemainingFamInNetwork);
                formData.estimateInfo.outOfPocketAmountRemainingFamOutOfNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountRemainingFamOutOfNetwork, formData.response271?.outOfPocketAmountRemainingFamOutOfNetwork);
                formData.estimateInfo.outOfPocketAmountRemainingIndInNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountRemainingIndInNetwork, formData.response271?.outOfPocketAmountRemainingIndInNetwork);
                formData.estimateInfo.outOfPocketAmountRemainingIndOutOfNetwork = await assignIfUndefined(formData.estimateInfo.outOfPocketAmountRemainingIndOutOfNetwork, formData.response271?.outOfPocketAmountRemainingIndOutOfNetwork);

                //save the 271 response data to the database as well
                const resp = await saveEstimation.savePatientBenefitManualEntry(formData.estimateInfo);

                if (!resp) {
                    popAlert("Error saving 271 elig check responses", "Error Saving Elig Check Responses");
                    return;
                }
            }     
        }   
    }

    async function saveEstimateManual() {
        const multipleCategories = coverageCategories.value?.split(',').length > 1;

        formData.estimateSavePartialPostData = {
            patientId: props.patientId,
            dateOfService: formData.dateOfService,
            payerEntityId: formData.selectedPayer?.payerEntityId,
            providerEntityId: providerEntityId.value,
            serviceLocationEntityId: locationEntityId.value,
            isManualEntry: true,
            isEstimateAccepted: false,
            chargeAmount: formData.chargeValue,
            allowedAmount: formData.allowedValue,
            firstCoverageCategory: formData.selectedServiceType?.serviceTypeCode,
            multipleCoverageCategoriesUsed:  multipleCategories ? coverageCategories.value : null,
            outputEstimationId: undefined
        }

        const resp = await saveEstimation.saveEstimatePartial(formData.estimateSavePartialPostData);

        if (!resp) {
            popAlert("Error saving partial estimate", "Error Saving Estimate");
            return;
        }

        //save the returned identity so I can use to add estimation lines later
        formData.estimateInfo = resp;

        //Now update the input fields for the calc with the 271 response if I have one
        await updateEstimateWith271Response();       

        //now save to EstimateLines table
        await saveEstimateLineManual();

    }

    async function saveEstimateLineManual() {
         if (undefined === formData.estimateInfo?.estimationId)  {
            return;
        }

        //prepare the post data
        formData.estimationLineSavePostData = {
            estimationId: formData.estimateInfo?.estimationId ?? 0,
            procedureCode: formData.codeSearchItem?.code ?? "",
            modifier: formData.modifierValue,
            diagnosisCode: formData.codeDxSearchItem?.code,
            units: formData.unitValue,
            chargeAmount: formData.chargeValue,
            allowedAmount: formData.allowedValue
        }

        //make sure procedure code is not already on the estimate before proceeding
        const hasProcedureCodeAlready = formData.estimationLinesResponseData?.find(item => item.procedureCode == formData.estimationLineSavePostData?.procedureCode);

        //save the estimation line data
        if (!hasProcedureCodeAlready) {
            const resp = await saveEstimation.saveEstimateLine(formData.estimationLineSavePostData);           
            formData.estimationLinesResponseData = resp ?? [];

            //update our totals
            formData.estimateInfo.chargeAmount = totalChargeAmountExtended.value;
            formData.estimateInfo.allowedAmount = totalAllowedAmountExtended.value;
        }           
    }

    async function updateEstimateLineManual() {
        //close the dialog
        formData.showAddLineManualEntry = false;

        if ( (undefined === formData.estimateInfo?.estimationId) || (0 == formData.estimationLineId) )  {
            return;
        }

        //prepare the post data
        formData.estimationLineUpdatePostData = {
            estimationLineId: formData.estimationLineId,
            estimationId: formData.estimateInfo?.estimationId ?? 0,
            procedureCode: formData.codeSearchItem?.code ?? "",
            modifier: formData.modifierValue,
            diagnosisCode: formData.codeDxSearchItem?.code,
            units: formData.unitValue,
            chargeAmount: formData.chargeValue,
            allowedAmount: formData.allowedValue
        }

        //update the estimation line data
        const resp = await saveEstimation.updateEstimateLine(formData.estimationLineUpdatePostData);            
        formData.estimationLinesResponseData = resp ?? [];      
        
        //update our totals
        formData.estimateInfo.chargeAmount = totalChargeAmountExtended.value;
        formData.estimateInfo.allowedAmount = totalAllowedAmountExtended.value;
    }

    function calcExtendedAmount(unitVal: number|undefined, amountVal: number|undefined) {
        return ((unitVal ?? 1) * (amountVal ?? 0));                
    }
  
    async function AddLineByApptType() {
        //check to see if there are already lines added.  If so, prompt that it's ok to clear these and start over
        formData.componentKey += 1;
        if ((formData.estimateInfo?.estimationId ?? 0) > 0 ) {
            const msg = "There are already lines for your estimate.  Do you want to start over? This will clear out current lines.";
            const result = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" });                

            if (result) {                
                const resp = await DeleteEstimation(formData.estimateInfo?.estimationId ?? 0);

                if (!resp) {
                    popAlert("Error deleting estimate: EstimateID = " + formData.estimateInfo?.estimationId, "Error Deleting Estimate");
                    return;
                }
                else {
                    //reset estimationId to 0
                    formData.estimateInfo = undefined;

                    //clear out any existing results
                    formData.estimationLinesResponseData = [];
                    formData.estimationLinesByApptType = [];
                    formData.selectedFutureAppt = undefined;
                    formData.estimationLineId = 0;

                    //show the modal dialog for appt type entry
                    formData.showAddLineApptType = true;
                }                   
            }
        }
        else {
            //trigger call to check if we have any future appts to choose from
            await LoadFutureAppointments();
            
            if ((formData.futureAppointments != undefined) && (formData.futureAppointments.length > 0)) {
                formData.showFutureAppt = true;
            }    

            //show the modal dialog for appt type entry
            formData.showAddLineApptType = true;    
        } 
    }

    async function AddLineByManualEntry() {
        //increment the key so new searches will be cleared
        formData.componentKey += 1;
       
        //reset props then show the modal dialog for manual entry
        formData.saveBtnLabel = "Save Line";
        formData.estimationLineId = 0;
        formData.codeSearchItem = undefined;
        formData.codeDxSearchItem = undefined;
        formData.unitValue = 1;
        formData.chargeValue = 0;
        formData.allowedValue = 0;
        formData.showAddLineManualEntry = true;
        formData.lineIsLimitedEdit = false;
    }

    async function onChangeApptTypeProcedure(apptTypeProcedure: IApptTypeProcedureSearchItem) {
        await LoadApptTypeCPTCount();
        formData.apptTypeProcedureSearchItem = apptTypeProcedure;        
        formData.selectedApptType = apptTypeProcedure.appointmentTypeName;
    }

    async function onChangeProvider(provider: IProviderSearchItem) {
        await changePayerProviderLocation();

        formData.providerSearchItem = provider;
        formData.selectedProviderName = (undefined === provider || !provider) ? undefined : provider.provider_name;
        await LoadChargeAllowedData();   
    }

    async function onChangeServiceLocation(serviceLocation: IServiceLocationSearchItem) {
        await changePayerProviderLocation();

        formData.serviceLocationSearchItem = serviceLocation;
        formData.selectedServiceLocation = serviceLocation.service_location_name;    
        await LoadChargeAllowedData();   
    }

    async function onChangeProcedureCode(codeSearch: ICodeSearchItem) {
        await LoadApptTypeCPTCount();
        formData.codeSearchItem = codeSearch;
        formData.selectedCPT = (undefined === codeSearch || !codeSearch) ? undefined : codeSearch.codeAndDesc;    

        await LoadChargeAllowedData();       

    }

    async function LoadChargeAllowedData() {
        //don't proceed to getting charge/allowed amount if we're only editing the line
        if ("Update Line" == formData.saveBtnLabel) {
            return;
        }

        //if we have all the inputs, get the allowed and charge amount
        //this will also be based on a company setting on whether they want to pull allowed based on payment aggregate or fee schedule

        //always get charge from the Fee Schedule, never the avg, regardless of company setting
        await LoadChargeAllowedFeeSchedule();
        formData.chargeValue = formData.chargeAllowedFeeSchedule?.chargeAmount ?? 0;

        if (CompanySettingAllowedMethod.FeeSchedule == formData.companySettingAllowed) {
            formData.allowedValue = formData.chargeAllowedFeeSchedule?.allowedAmount ?? 0;
        }
        else {
            await LoadChargeAllowedAggregate();
            formData.allowedValue = formData.chargeAllowedAggregate?.avgAllowedAmount ?? 0;
        }
    }

    async function onChangeCodeDx(codeSearch: ICodeSearchItem) {
        await LoadApptTypeCPTCount();
        formData.codeDxSearchItem = codeSearch;
        formData.selectedDx = (undefined === codeSearch || !codeSearch) ? undefined : codeSearch.codeAndDesc;   
    }    

    function saveLines() {
        const errorMessage: string[]=[];

        if ("" === (formData.dateOfService ?? "")) {
            errorMessage.push("Date of Service");
        }

        if (0 === (payerEntityId.value ?? 0)) {
            errorMessage.push("Payer");
        }

        if (0 === (providerEntityId.value ?? 0)) {
            errorMessage.push("Provider");
        }

        if (0 === (locationEntityId.value ?? 0)) {
            errorMessage.push("Service Location");
        }

        if ( (!formData.selectedFutureAppt) && (0 === (apptTypeId.value ?? 0)) ) {
            errorMessage.push("'Select Appt for Estimate' OR 'Appt Type'. Must choose one.");
        }

        if (errorMessage.length > 0) {
            popAlert("Required fields are missing:<br>" + errorMessage.map(msg => ` - ${msg}`).join("<br>"));
            return;
        }

        //close the dialog
        formData.showAddLineApptType = false;

        formData.estimationLinesByApptTypeInputs = {appointmentTypeId: apptTypeId.value, 
            payerEntityId: payerEntityId.value, 
            providerEntityId: providerEntityId.value,
            locationEntityId: locationEntityId.value};

        GetEstimationLinesByApptType(formData.estimationLinesByApptTypeInputs);        
    }

    async function closeEntryModal() {
        formData.showAddLineManualEntry = false;
    }

    async function saveLinesManual() {
        const errorMessage: string[]=[];

        if ((0 === (providerEntityId.value ?? 0)) && ("" === formData.selectedProviderName)) {
            errorMessage.push("Provider");
        }
        if ((0 === (locationEntityId.value ?? 0)) && ("" === formData.selectedServiceLocation)) {
            errorMessage.push("Service Location");
        }
        if (undefined === formData.selectedCPT) {
            errorMessage.push("CPT Code");
        }
        if ("" === (formData.dateOfService ?? "")) {
            errorMessage.push("Date of Service");
        }
        if (0 === (formData.unitValue ?? 0)) {
            errorMessage.push("Unit");
        }
        if (0 === (formData.chargeValue ?? 0)) {
            errorMessage.push("Charge per Unit");
        }
        if (0 === (formData.allowedValue ?? 0)) {
            errorMessage.push("Allowed per Unit");
        }
        if (errorMessage.length > 0) {            
            popAlert("Required fields are missing:<br>" + errorMessage.map(msg => ` - ${msg}`).join("<br>"), "Error Saving Estimation Line");
            formData.addLineMissingRequiredFields = true;
            return;
        }
        else {
            formData.addLineMissingRequiredFields = false;
        }

        //check if we have an estimate id already and user is just adding lines to it
        if (undefined === formData.estimateInfo) {
            await saveEstimateManual();
        }   

        if (formData.estimationLineId > 0) {
            await updateEstimateLineManual();
        }
 
        if (undefined != formData.estimateInfo && 0 === formData.estimationLineId) {
            await saveEstimateLineManual();
            formData.selectedCPT = undefined;
        }
    }

    async function saveLineAndClose() {
        await saveLinesManual();

        if (!formData.addLineMissingRequiredFields) {
            await closeEntryModal();
        }
    }

    async function saveLinesManualAndAddNewLine() {
        await saveLinesManual();

        if (!formData.addLineMissingRequiredFields) {
            await AddLineByManualEntry();
        }
    }

    async function DeleteLine(lineId: number, estId: number, isManualEntry: boolean)
    {
        const msg = "Are you sure you want to delete this line?";
        const result = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" });          

        if (result) {
            formData.estimationLineDeletePostData = {
                estimationLineId: lineId,
                estimationId: estId
            }

            const resp = await deleteOperations.deleteEstimationLine(formData.estimationLineDeletePostData);

            if (!resp) {
                popAlert("Error deleting estimate Id " + estId + " and estimate line id " + lineId, "Error Deleting Estimation Line");
                return;
            }

            await CalculateEstimate(false);
            
            //update the object to remove the deleted line client-side rather than bringing back new dataset from server
            formData.estimationLinesResponseData = formData.estimationLinesResponseData.filter(item => item.estimationLineId != lineId);
        }        
    }

    async function DeleteEstimation(estId: number)
    {
        //delete the selected EstimationId and any lines attached to it since user wants to start over
        formData.estimationDeletePostData = {
            estimationId: estId
        }

        const resp = await deleteOperations.deleteEstimation(formData.estimationDeletePostData);     
        
        if (!resp) {
            popAlert("Error deleting estimate id " + formData.estimateInfo?.estimationId, "Error Deleting Estimate");
            return;
        }

        return resp;
    }

    function EditLine(lineData: IEstimationLineResponse) {
        formData.saveBtnLabel = "Update Line";

        //if they used appt type to add lines, only certain things can be updated on line
        formData.lineIsLimitedEdit = ((lineData.isManualEntry) || (0 === lineData.allowedAmount) || (undefined === lineData.allowedAmount)) ? false : true;

        //update the properties bound to v-model
        formData.estimationLineId = lineData.estimationLineId;
        formData.selectedCPT = lineData.procedureCode;
        formData.selectedDx = lineData.diagnosisCode;
        formData.dateOfService = lineData.dateOfService;
        formData.modifierValue = lineData.modifier ?? "";
        formData.unitValue = lineData.units ?? 1;
        formData.chargeValue = lineData.chargeAmount ?? 0;
        formData.allowedValue = lineData.allowedAmount ?? 0;

        //populate the procedure code search item with what we know from the edited line
        formData.codeSearchItem = {
            code: lineData.procedureCode,
            codeShortDesc: lineData.procedureDesc,
            codeAndDesc: lineData.procedureCode + " " + lineData.procedureDesc
        }

        //populate the diagnosis code search item with what we know from the edited line
        formData.codeDxSearchItem = {
            code: lineData.diagnosisCode,
            codeShortDesc: lineData.diagnosisDesc,
            codeAndDesc: lineData.diagnosisCode + " " + lineData.diagnosisDesc
        }

        //display modal
        onChangeProcedureCode(formData.codeSearchItem);
        onChangeCodeDx(formData.codeDxSearchItem);
        formData.showAddLineManualEntry = true;
    }

    function EstimateHistory() {
        LoadEstimateHistory();

        formData.showEstimateHistory = true;
    }

    async function PrintableView(estId: number, isEstAccepted: boolean, isEstimateInContext?: boolean) {
        formData.showEstimateHistory = false;
        formData.printEstimationId = estId;
        formData.printIsEstimateAccepted = isEstAccepted;

        if (isEstimateInContext) {
            await CalculateEstimate(isEstAccepted);
        }

        //let's first check to see if we have a signed estimate from MML
        const signedResp = await costEstimator.getMmlSignedEstimateData(estId);
        if (undefined !== signedResp) {
            //we have a signed estimate, so let's call it directly, this api returns the file
            const url = `https:${getOfficeMdBaseUri()}api/Patient/CostEstimator/GetMMLSignedEstimatePdf/${estId}`;
            window.open(url, "SignedEstimate", "popup");
            return;
        }

        const resp = await costEstimator.getPrintableRazorView(estId);

        if (resp === undefined || resp === null) {
            popAlert(`We could not retrieve the printable view of estimate Id ${estId}`);
            return;
        }

        // Ensure resp is a string, since the DOM element innerHTML requires a string (not string|undefined)
        const safeResp: string = resp ?? "";

        // Using a non-null assertion, but will still check that element exists
        const reportHtmlElement = document.getElementById("ReportHTML");

        if (reportHtmlElement) {
            reportHtmlElement.innerHTML = safeResp;
        } else {
            popAlert(`We could not retrieve the printable estimate Id ${estId}`);
        }

        window.open('/officemd/datapages/pdf_report.asp?txtReportSection=HTML&txtReportField=ReportHTML&txtReportInput=costestimate&IB=Y&RN=MedicalEstimate', 'EstimateWindow', 'height=700,width=900'); 
    }
 
    async function EditEstimate(estId: number) {
        const resp = await costEstimator.getEstimationById(estId);

        if (!resp) {
            popAlert("Estimate Id " + estId + " not found in history", "Error Editing Estimate History");
        }

        formData.estimateInfo = resp;  
        formData.savedManualEntry = formData.estimateInfo.isManualEntry; 

        const result = await costEstimator.getEstimateLinesById(estId);

        if (!result) {
            popAlert("No Lines for Estimate Id " + estId + " found in history", "Error Editing Estimate Lines History");
        }
        formData.estimationLinesResponseData = result;

        //update the coverage type, insurance plan, dos, provider, location
        formData.selectedServiceType = formData.serviceTypeCodeList?.find(item => item.serviceTypeCode == formData.estimateInfo?.coverageCategoryServiceTypeCode);
        formData.selectedPayer = formData.patientPayers?.find(item => item.payerEntityId == formData.estimateInfo?.payerEntityId);
        formData.dateOfService = DateUtils.prettyDate(formData.estimateInfo.dateOfService);

        //get entity info for the selected Provider and Location so that we can populate the search boxes
        const providerInfo = await entityInfo.getEntityInfoByDatabaseId({ id: formData.estimateInfo.providerEntityId ?? 0, idType: DatabaseIdTypes.entity_id });
        const locationInfo = await entityInfo.getEntityInfoByDatabaseId({ id: formData.estimateInfo.serviceLocationEntityId ?? 0, idType: DatabaseIdTypes.entity_id});

        formData.selectedProviderName = providerInfo ? providerInfo.entityName : undefined;
        formData.selectedServiceLocation = locationInfo ? locationInfo.entityName : undefined;

        formData.showEstimateHistory = false;
        formData.showSummary = true;
    }

    async function DeleteEstimate(estId: number, isPreConfirmed: boolean, clearPayer: boolean) {
        let confirmResult = false;
        if (!isPreConfirmed) {
            const msg = "Are you sure you want to delete this estimate (Id " + estId + ")?";
            confirmResult = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" }); 
        }
        else {
            confirmResult = true;
        }    

        if (confirmResult) {                
            const resp = await DeleteEstimation(estId);

            if (!resp) {
                popAlert("Error deleting estimate: EstimateID = " + estId, "Error Deleting Estimate");
                return;
            }
            else {
                //if user just deleted current proposed estimate, clear out the estimateinfo, estimationline objects and hide summary
                if (estId === formData.estimateInfo?.estimationId) {
                    formData.estimateInfo = undefined;
                    formData.estimationLinesResponseData = [];
                    formData.showSummary = false;
                    formData.dateOfService = DateUtils.getNoTzToday();
                    formData.selectedPayer = clearPayer ? formData.patientPayers?.find(item => item.insuredCoverage == 1) : formData.selectedPayer;
                    formData.selectedProviderName = undefined;
                    formData.selectedServiceLocation = undefined;
                }

                //force reload of history
                LoadEstimateHistory();
            }
        }
    }

    function OnFutureApptUpdate() {
        //update the form based on selected future appt
        formData.dateOfService = DateUtils.toDisplayDateString(formData.selectedFutureAppt?.apptStartDate, DateDisplayFormat.NumericDate);
        formData.selectedProviderName = formData.selectedFutureAppt?.providerName;
        formData.selectedServiceLocation = formData.selectedFutureAppt?.locationName;

    }

    function CheckEligibility() {
        ModalWindowOpen({screenID: AppScreens.PatientEligibility, urlParmData: {EID: formData.patientEntity?.entity_id.toString()}}); 
    }

    function BenefitsManualEntry() {
        formData.showPatientBenefitManualEntry = true;
    }

    async function NetworkOptions(network: NetworkTypes) {
        formData.selectedNetwork = network;     
        await CalculateEstimate(formData.estimateInfo?.isEstimateAccepted ?? false);   
    }

    function ToggleAllowedUsage(allowedUsage: CompanySettingAllowedMethod) {        
        formData.selectedAllowedUsage = allowedUsage;

        //if user toggled back to Fee Schedule, reset days back to company setting
        if (CompanySettingAllowedMethod.FeeSchedule == allowedUsage) {
            formData.selectedDaysToAggregate = parseInt(formData.companySettingDaysToAggregate, 10);
        }

        reCalculateAllowedAmount();
    }

    async function reCalculateAllowedAmount() {
        if (CompanySettingAllowedMethod.PaymentAggregate == formData.selectedAllowedUsage) {
            await LoadChargeAllowedAggregate();
            
            if (formData.chargeAllowedAggregate) {
                formData.allowedValue = formData.chargeAllowedAggregate?.avgAllowedAmount;
                formData.showAggregateRecordCount = true;
            }
        }

        if (CompanySettingAllowedMethod.FeeSchedule == formData.selectedAllowedUsage) {
            await LoadChargeAllowedFeeSchedule();

            if (formData.chargeAllowedFeeSchedule) {
                formData.allowedValue = formData.chargeAllowedFeeSchedule.allowedAmount;
                formData.showAggregateRecordCount = false;
            }
        }
    }

    async function CalculateEstimate(isAccepted: boolean) {
        //keep the original network selection to toggle it back afterwards, but get patient and insurance totals for both in and out of network so both can be saved to db
        const origNetworkSelection = formData.selectedNetwork;
        formData.selectedNetwork = NetworkTypes.In;
        const patAmountInNetwork = calcTotalPatient.value;
        const insAmountInNetwork = calcTotalInsurance.value;
        formData.selectedNetwork = NetworkTypes.Out;
        const patAmountOutOfNetwork = calcTotalPatient.value;
        const insAmountOutOfNetwork = calcTotalInsurance.value;
        formData.selectedNetwork = origNetworkSelection;

        formData.estimationSaveCalculationInputs = {
            estimationId: formData.estimateInfo?.estimationId,
            coverageCategoryServiceTypeCode: formData.selectedServiceType?.serviceTypeCode,
            patientTransactionId: formData.lastEligCheck?.patientTransactionId,
            isEstimateAccepted: isAccepted,
            patientAmountInNetworkEstimate: patAmountInNetwork,
            patientAmountOutOfNetworkEstimate: patAmountOutOfNetwork,
            insuranceAmountInNetworkEstimate: insAmountInNetwork,
            insuranceAmountOutOfNetworkEstimate: insAmountOutOfNetwork,
            networkType: origNetworkSelection,
            totalChargeAmount: totalChargeAmountExtended.value,
            totalAllowedAmount: totalAllowedAmountExtended.value
        }

        SaveCalculation(formData.estimationSaveCalculationInputs);

        formData.showSummary = true;
    }

    async function AcceptEstimate() {
        const msg = "Are you sure you want to accept this estimate as final? This will lock the estimate and prevent future editing. This will also send the estimate to MML if the patient has an account.";
        const result = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" });     

        if (result) {  
            await CalculateEstimate(true);

            //We now want to send estimates to MML upon acceptance (it was sending only when an accepted estimate was printed)
            formData.printIsEstimateAccepted = true;

            //Get the HTML of the estimate first
            const resp = await costEstimator.getPrintableRazorView(formData.estimateInfo?.estimationId ?? 0);

            if (!resp) {
                popAlert("There was a problem saving the estimate to MML.");
                return;
            }

            formData.convertToHtmlParams = {
                input: "costestimate",
                pdfFileName: "Medical Estimate",
                sectionName: "HTML",
                htmlContent: resp
            }
            const pdfInfo = await costEstimator.convertHtmlToPdf(formData.convertToHtmlParams);

            if (!pdfInfo) {
                popAlert("We could not retrieve the PDF of this estimate to send to MML", "Error Retrieving Estimate PDF after acceptance");
                return;
            }

            //Update the pfd_report_id, then save this to MML
            formData.pdfReportId = pdfInfo.pdf_report_id;
            formData.printEstimationId = formData.estimateInfo?.estimationId;
            SaveToMml();
        }        
    }

    async function saveManualEntryBenefits() {
        //We cannot really require entry of any specific field, so no further validation can be added here
        if (!formData.estimateInfo) {
            return;
        }

        const resp = await saveEstimation.savePatientBenefitManualEntry(formData.estimateInfo); 

        if (!resp) {
            popAlert("Error saving patient benefit manual entry", "Error Saving Benefit Manual Entry");
            return;
        }

        formData.savedManualEntry = true;

        formData.estimateInfo = resp;

        //save the Calculation automatically too
        await CalculateEstimate(formData.estimateInfo.isEstimateAccepted);

        //close the modal
        formData.showPatientBenefitManualEntry = false;
    } 

    async function SaveCalculation(inputData: IEstimationSaveCalculationInputs) {
        if (!inputData) {
            return;
        }

        const resp = await saveEstimation.saveCalculation(inputData);

        if (!resp) {
            popAlert("Error saving estimate calculation", "Error Saving Estimate Calculation");
            return;
        }

        formData.estimateInfo = resp;
    }

    async function closeEstimateHistoryDialog() {
        formData.showEstimateHistory = false;
    }

    function StartNewEstimate() {
        //reset appropriate objects so user can start a new estimate
        formData.estimateInfo = undefined;
        formData.selectedCPT = undefined;
        formData.selectedDx = undefined;
        formData.selectedServiceLocation = undefined;
        formData.selectedProviderName = undefined;
        formData.estimationLinesResponseData = [];
        formData.selectedFutureAppt = undefined;
        formData.providerEntityInfo = undefined;
        formData.locationEntityInfo = undefined;
        formData.allowedValue = 0;
        formData.chargeValue = 0;
        formData.showSummary = false;
    }

    function sinkMessageForMmlSave(event: MessageEvent) {
        //Added a broadcast channel to pdf_report.asp for PatientCostEstimator that will return the pdf_report_id that was just built
        //If the estimate is accepted, need to send this pdf to patient's MML account
        formData.pdfReportId = event.data.pdf_report_id;
        SaveToMml();
    }

    async function sinkMessageForEligibilityCheck(event: MessageEvent) {
        //Added a broadcast channel to PatientEligibility.asp for PatientCostEstimator that will return the patientTransactionId that was just built on the elig check
        //If this is broadcast, that means a new elig check was performed, and I want to recheck the last elig
        if ((event.data.patientTransactionId || 0) > 0) {
            await LoadLastEligCheck();
        }
    }

    async function SaveToMml() {
        //Logic to send printed estimate PDF to MML - only send accepted estimates
        if ((formData.pdfReportId != 0) && (formData.printIsEstimateAccepted)) {
            formData.estimationSaveToMmlInputs = {
                estimationId: formData?.printEstimationId ?? 0,
                pdfReportId: formData.pdfReportId ?? 0,
                patientId: props.patientId
            }

            return await saveEstimation.saveEstimateToMml(formData.estimationSaveToMmlInputs);
        }
    }

    function GetSignatureInfo(signatureDate: Date, estimateId: number | undefined) {
        if (undefined == estimateId) {
            popAlert("No estimate Id passed to get signature info.");
            return;
        }
        formData.patientElectronicSignatureText = `The patient electronically signed this estimate (${estimateId}) in MML on ${DateUtils.toDisplayDateString(signatureDate, DateDisplayFormat.NumericDate)} ${DateUtils.prettyTime(signatureDate)}`
        formData.showPatientSignatureModal = true;
    }

    function closePatientSignatureModal() {
        formData.showPatientSignatureModal = false;
    }
   
</script>


<template>    
    <div class="container-fluid">
        <!--Last Estimate Section-->
        <div v-if="formData.patientLastEstimate">
            <div class="row">
                <div class="col ps-0 fw-bold text-isalus-primary fs-5 pt-2 mb-2">
                    Last Estimate
                    <em @click="EstimateHistory()" class="fa fa-history ps-1" style="cursor: pointer;" aria-hidden="true" title="Estimate History"></em>
                </div>
            </div>

            <table class="table table-bordered table-responsive" aria-describedby="Last Estimate table">
                <thead class="bg-light border-isalus-primary text-isalus-primary">
                    <tr>
                        <th scope="col">Appt Type</th>
                        <th scope="col">Provider</th>
                        <th scope="col">Insurance</th>
                        <th scope="col" class="text-end">Allowed</th>
                        <th scope="col" class="text-end">Patient</th>
                        <th scope="col" class="text-end">Insurance</th>
                        <th scope="col" class="text-center">Quote Date</th>
                    </tr>
                </thead>
                <tbody class="border-isalus-primary">
                    <tr>
                        <td @click="PrintableView(formData.patientLastEstimate?.estimationId ?? 0, formData.patientLastEstimate?.isEstimateAccepted ?? false, false)" class="text-decoration-underline" style="cursor: pointer;" title="Printable View of Last Estimate">{{formData.patientLastEstimate?.apptTypeDesc}}</td>
                        <td>{{formData.patientLastEstimate?.providerName}}</td>
                        <td>{{formData.patientLastEstimate?.payerName}}</td>
                        <td class="text-end">{{ToMoneyStr(formData.patientLastEstimate?.allowedAmount)}}</td>
                        <td class="text-end">{{ToMoneyStr(formData.patientLastEstimate?.patientAmountInNetwork)}}</td>
                        <td class="text-end">{{ToMoneyStr(formData.patientLastEstimate?.insuranceAmountInNetwork)}}</td>
                        <td class="text-center">{{DateUtils.toDisplayDateString(formData.patientLastEstimate?.estimationCreateDate, DateDisplayFormat.NumericDate)}} {{DateUtils.prettyTime(formData.patientLastEstimate?.estimationCreateDate)}}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!--Estimation Lines Section-->
        <div class="row">
            <div class="col ps-3 fw-bold text-isalus-primary fs-5 pt-4 mb-2">
                New Estimation Lines
                <em v-if="formData.estimateInfo != undefined" @click="StartNewEstimate()" class="fa fa-calculator ps-1" style="cursor: pointer;" aria-hidden="true" title="Start a New Estimate"></em>
            </div>
        </div>

        <div class="row mt-2">
            <div class="col col-md-5">
                <TitledRow title="Date of Service*">
                    <DatePickerNoTZ v-model:value="formData.dateOfService" :clearable="false" :required="true" :display-format="DateDisplayFormat.NumericDate"/>
                </TitledRow>
            </div>
            <div class="col col-md-7">
                <TitledRow title="Provider*">
                    <SearchProvider v-model:defaultSearchText="formData.selectedProviderName" :label="formData.labelEmpty" :enable-auto-search="true" :show-clear-search="true" @onChange="onChangeProvider" />
                </TitledRow>
            </div>
        </div>

        <div class="row">
            <div class="col col-md-5">
                <TitledRow title="Payer*">
                    <select class="form-select" v-model="formData.selectedPayer" required="true" @change="changePayerProviderLocation">  
                        <option v-for="payer in formData.patientPayers" :value="payer">                                
                            {{payer.insuredCoverageDesc}} - {{payer.payerName}} - {{payer.insuredIdCode}}
                        </option>
                    </select>
                </TitledRow>
            </div>
            <div class="col col-md-7">
                <TitledRow title="Service Location*">
                    <SearchServiceLocation 
                        v-model:defaultSearchText="formData.selectedServiceLocation" 
                        :label="formData.labelEmpty" 
                        :enable-auto-search="true" 
                        :show-clear-search="true" 
                        :limitToClaimLocations="true"
                        @onChange="onChangeServiceLocation" />
                </TitledRow>
            </div>
        </div>
        <div v-if="!hasPatientPayers" class="row ps-3 pt-2 text-danger">
            Patient must be assigned at least one payer before proceeding with a Cost Estimate.<br>
            If self-pay, please assign the self-pay plan.
        </div>
                
        <!--Modal Dialog to Add Line by Appt Type-->
        <ModalDialog 
            v-model:show="formData.showAddLineApptType"
            :size="ModalSize.XL"
        >
            <template v-slot:title>Add Line By Appointment Type</template>

            <div class="row" v-show="formData.showFutureAppt">
                <div class="col">
                    <TitledRow title="Select Appt for Estimate" content-class="col-md-9">
                        <select class="form-select" v-model="formData.selectedFutureAppt" @change="OnFutureApptUpdate" required="true" title="Showing next 3 future appts where appt type is mapped to a procedure code">  
                            <option v-for="appt in formData.futureAppointments" :value="appt">
                                {{appt.appointmentTypeDesc}} -   
                                {{DateUtils.toDisplayDateString(appt.apptStartDate, DateDisplayFormat.NumericDate)}} {{DateUtils.prettyTime(appt.apptStartDate)}} -  
                                {{appt.providerName}} - 
                                {{appt.locationName}}
                            </option>
                        </select>
                    </TitledRow>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col">
                    <TitledRow title="Appt Type" content-class="col-md-9">
                        <SearchApptTypeProcedure v-model:defaultSearchText="formData.selectedApptType" :label="formData.labelEmpty" :enable-auto-search="true" :show-clear-search="true" :key="formData.componentKey" @onChange="onChangeApptTypeProcedure" />
                    </TitledRow>
                </div>
            </div>
            <template v-slot:footer>
                <button :disabled="!formData.showSaveLinesButton" @click="saveLines" type="button" class="btn btn-primary">Save Line(s)</button>
            </template>
        </ModalDialog>

        <!--Modal Dialog to Add Line by Manual Entry-->
        <ModalDialog 
            v-model:show="formData.showAddLineManualEntry"
            :size="ModalSize.XL"
        >
            <template v-slot:title>Add Line By Manual Entry</template>

            <div class="row mt-3">
                <div class="col-md-10">
                    <TitledRow title="CPT Code*" content-class="col-md-9">
                        <SearchProcedureCode :key="formData.componentKey" v-model:defaultSearchText="formData.selectedCPT" :label="formData.labelEmpty" :enable-auto-search="true" :show-clear-search="true" placeholder="Search CPT Code" @onChange="onChangeProcedureCode" />
                    </TitledRow>
                </div>
            </div> 
            <div class="row mt-3">
                <div class="col-md-10">
                    <TitledRow title="Diagnosis Code" content-class="col-md-9">
                        <SearchDiagnosisCode :key="formData.componentKey" v-model:defaultSearchText="formData.selectedDx" :label="formData.labelEmpty" :enable-auto-search="true" :show-clear-search="true" placeholder="Search Diagnosis Code" @onChange="onChangeCodeDx" />
                    </TitledRow>
                </div>
            </div>                
            <div class="row mt-2">
                <div class="col-md-10">
                    <TitledRow title="Modifier" content-class="col-md-4">
                        <TextInput      
                            v-model:text-value="formData.modifierValue"                            
                        />
                    </TitledRow>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col-md-10">
                    <TitledRow title="Unit*" content-class="col-md-4">
                        <IntegerInput 
                            v-model:numberValue="formData.unitValue" 
                            :maxLength=6 
                            :startingRange=1
                            :endingRange=99999
                            :positive=true
                            :required=true
                            :alignment=Alignment.left
                            LeftAddon="fa-solid fa-hashtag"
                            :error-display="true"
                        /> 
                    </TitledRow>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col-md-10">
                    <TitledRow title="Charge per Unit*" content-class="col-md-4">
                        <CurrencyInput 
                            v-model:numberValue="formData.chargeValue" 
                            :startingRange=0
                            :endingRange=99999.99
                            :positive=true
                            :required=true
                            :alignment=Alignment.left
                            placeholder="Charge amount" 
                            :error-display="true"    
                        />
                    </TitledRow>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col-md-10">
                    <TitledRow title="Allowed per Unit*" content-class="col-md-4">
                        <CurrencyInput 
                            v-model:numberValue="formData.allowedValue" 
                            :startingRange=0
                            :endingRange=99999.99
                            :positive=true
                            :required=true
                            :alignment=Alignment.left
                            placeholder="Allowed amount" 
                            :disabled="formData.lineIsLimitedEdit"
                            :error-display="true"                            
                        />
                    </TitledRow>
                </div>
            </div>

            <div v-if="!formData.lineIsLimitedEdit" class="row mt-2">
                <div class="col-md-10">
                    <TitledRow title="Allowed Amount Usage" content-class="col-md-9">            
                        <div class="btn-group" role="group" aria-label="Toggle Allowed Amount Usage" title="Toggle to use either Payment Aggregated Allowed Amount or Fee Schedule Allowed Amount in your estimate. Defaults to your company setting.">
                            <input type="radio" v-model="formData.selectedAllowedUsage" @click="ToggleAllowedUsage(CompanySettingAllowedMethod.PaymentAggregate)" class="btn-check" id="btnAllowedUseAggregate" value="0">
                            <label class="btn btn-outline-primary btn-sm" for="btnAllowedUseAggregate">Aggregate</label>

                            <input type="radio" v-model="formData.selectedAllowedUsage" @click="ToggleAllowedUsage(CompanySettingAllowedMethod.FeeSchedule)" class="btn-check" id="btnAllowedUseFeeSchedule" value="1">
                            <label class="btn btn-outline-primary btn-sm" for="btnAllowedUseFeeSchedule">Fee Schedule</label>
                        </div>
                    </TitledRow>
                    <TitledRow v-if="CompanySettingAllowedMethod.PaymentAggregate == formData.selectedAllowedUsage" title="Days to Aggregate" content-class="col-md-4">
                        <IntegerInput 
                            v-model:numberValue="formData.selectedDaysToAggregate" 
                            :maxLength=3 
                            :startingRange=1
                            :endingRange=365
                            :positive=true
                            :alignment=Alignment.left
                        /> 
                        <div class="row" v-if="undefined !== errorAggregateCalc">
                            <TitledRow title="" content-class="col-md-12 mb-4 text-danger">
                                {{ errorAggregateCalc }}
                            </TitledRow>
                        </div>                        
                    </TitledRow>
                    <TitledRow title="" content-class="col-md-4">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Refresh"
                            :size="ButtonDisplaySize.XS" 
                            :text="'Update Allowed Amount'"
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                            :spinText="'Recalculating'"
                            :performAction="reCalculateAllowedAmount"
                        />
                    </TitledRow>
                    <TitledRow v-if="formData.showAggregateRecordCount" title="Records Used in Aggregate" content-class="col-md-4">
                        {{ formData.chargeAllowedAggregate?.avgAllowedCount }}                     
                    </TitledRow>
                    
                </div>
            </div>

            <template v-slot:footer>
                <ChoiceButton 
                    :size="ButtonDisplaySize.Default"
                    :action="ActionButtonStyle.NoIcon" 
                    :text="formData.saveBtnLabel" 
                    title="Save Line" 
                    @click="saveLineAndClose" />
                <ChoiceButton 
                    :size="ButtonDisplaySize.Default"
                    :action="ActionButtonStyle.NoIcon" 
                    text="Save & New Line" 
                    title="Save then Add New Line" 
                    @click="saveLinesManualAndAddNewLine" />
            </template>     
        </ModalDialog>

        <!--Modal Dialog to Add Patient Benefits by Manual Entry-->
        <ModalDialog 
            v-model:show="formData.showPatientBenefitManualEntry"
            :size="ModalSize.XL"
        >
            <template v-slot:title>Patient Benefit Manual Entry</template>

            <div v-if="formData.estimateInfo">
                <div class="row">
                    <div class="col-md-10 mt-2 fw-bold ps-2 text-isalus-primary">In Network</div>
                    <div v-if="!formData.estimateInfo.isEstimateAccepted" class="col-md-2 text-end">
                        <button @click="saveManualEntryBenefits" type="button" class="btn btn-primary">Save & Exit</button>
                    </div>
                </div> 

                <div v-if="!hasMultipleCopayInNetwork" class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="CoPay" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.copayAmountInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="CoPay" 
                                :error-display="true"   
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5"></div>
                </div>

                <div v-if="hasMultipleCopayInNetwork" class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="CoPay" :title-width="5">
                            <v-select 
                                v-model="formData.estimateInfo.copayAmountInNetwork"
                                :options="copayDataInNetwork" 
                                :reduce="item => +item.copayAmount" label="frequencyDescription" 
                                :multiple="false" 
                                :searchable="true"
                                :filterable="true"
                                :closeOnSelect="true"
                                :select-on-tab="true" />
                        </TitledRow>
                    </div>
                    <div class="col-md-5"></div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Deductible Individual" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountIndInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Individual" 
                                :error-display="true"    
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountRemainingIndInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Remaining" 
                                :error-display="true" 
                            />
                        </TitledRow>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Deductible Family" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountFamInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Family" 
                                :error-display="true"   
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountRemainingFamInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Remaining" 
                                :error-display="true" 
                            />
                        </TitledRow>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Co-Insurance (%)" :title-width="5">
                            <IntegerInput
                                v-model:number-value="formData.estimateInfo.coinsurancePctInNetwork"
                                :starting-range=0
                                :ending-range=100
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Co-Insurance"
                                :error-display=true
                                RightAddon="fa-solid fa-percent"
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5"></div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Out of Pocket Individual" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountIndInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Individual" 
                                :error-display="true"   
                            />                            
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountRemainingIndInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Remaining" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Out of Pocket Family" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountFamInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Family" 
                                :error-display="true"   
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountRemainingFamInNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Remaining" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                </div>

                <!--OUT OF NETWORK-->
                <hr>
                <div class="row mt-2 fw-bold ps-2 text-isalus-primary">Out of Network</div>

                <div v-if="!hasMultipleCopayOutOfNetwork" class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="CoPay" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.copayAmountOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="CoPay" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5"></div>
                </div>

                <div v-if="hasMultipleCopayOutOfNetwork" class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="CoPay" :title-width="5">
                            <v-select 
                                v-model="formData.estimateInfo.copayAmountOutOfNetwork"
                                :options="copayDataOutOfNetwork" 
                                :reduce="item => +item.copayAmount" label="frequencyDescription" 
                                :multiple="false" 
                                :searchable="true"
                                :filterable="true"
                                :closeOnSelect="true"
                                :select-on-tab="true" />
                        </TitledRow>
                    </div>
                    <div class="col-md-5"></div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Deductible Individual" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountIndOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Individual" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountRemainingIndOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Remaining" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Deductible Family" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountFamOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Family" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.deductibleAmountRemainingFamOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Deductible Remaining" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Co-Insurance (%)" :title-width="5">
                            <IntegerInput
                                v-model:number-value="formData.estimateInfo.coinsurancePctOutOfNetwork"
                                :starting-range=0
                                :ending-range=100
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Co-Insurance"
                                :error-display=true
                                RightAddon="fa-solid fa-percent"
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5"></div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Out of Pocket Individual" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountIndOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Individual" 
                                :error-display="true"    
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountRemainingIndOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Remaining" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-7">
                        <TitledRow title="Out of Pocket Family" :title-width="5">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountFamOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Family" 
                                :error-display="true"   
                            />
                        </TitledRow>
                    </div>
                    <div class="col-md-5">
                        <TitledRow title="Remaining" :title-width="3">
                            <CurrencyInput 
                                v-model:number-value="formData.estimateInfo.outOfPocketAmountRemainingFamOutOfNetwork" 
                                :startingRange=0
                                :endingRange=99999.99
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Out of Pocket Remaining" 
                                :error-display="true"  
                            />
                        </TitledRow>
                    </div>
                </div>
            </div>

            <template v-slot:footer>
                <div v-if="formData.estimateInfo && !formData.estimateInfo.isEstimateAccepted" class="col-md-2 text-end">
                    <button @click="saveManualEntryBenefits" type="button" class="btn btn-primary">Save & Exit</button>
                </div>
            </template>
        </ModalDialog>

        <!---Modal Dialog for Estimate History-->
        <ModalDialog 
            v-model:show="formData.showEstimateHistory"
            :size="ModalSize.XL"
        >
            <template v-slot:title>Estimate History</template>

            <div v-if="formData.estimateHistory">
                <div v-if="0 === formData.estimateHistory?.length">
                    No prior estimates found for this patient.
                </div>
                <div v-else style="overflow: auto;">
                    <table class="table table-bordered table-responsive table-striped table-hover mt-3" aria-describedby="Estimation Lines table">
                        <thead class="bg-light border-isalus-primary text-isalus-primary">
                            <tr>
                                <th scope="col">Id</th>
                                <th scope="col">DOS</th>
                                <th scope="col">Date Created</th>
                                <th scope="col">Created By</th>
                                <th scope="col">Status</th>
                                <th scope="col" class="text-end">Charge</th>
                                <th scope="col" class="text-end">Allowed</th>
                                <th scope="col" class="text-end">Patient Estimate</th>
                                <th scope="col" class="text-end">Insurance Estimate</th>
                                <th scope="col" class="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="border-isalus-primary">
                            <tr v-for="item in formData.estimateHistory">
                                <td>{{item.estimationId}}</td>
                                <td>{{DateUtils.toDisplayDateString(item.dateOfService, DateDisplayFormat.NumericDate)}}</td>
                                <td>{{DateUtils.toDisplayDateString(item.estimationCreateDate, DateDisplayFormat.NumericDate)}}</td>
                                <td>{{item.userId}}</td>
                                <td>{{item.isEstimateAccepted ? "Accepted" : "Proposed"}}</td>
                                <td class="text-end">{{ToMoneyStr(item.chargeAmount)}}</td>
                                <td class="text-end">{{ToMoneyStr(item.allowedAmount)}}</td>
                                <td class="text-end">{{(NetworkTypes.Out == item.networkType) ? ToMoneyStr(item.patientAmountOutOfNetworkEstimate) : ToMoneyStr(item.patientAmountInNetworkEstimate)}}</td>
                                <td class="text-end">{{(NetworkTypes.Out == item.networkType) ? ToMoneyStr(item.insuranceAmountOutOfNetworkEstimate) : ToMoneyStr(item.insuranceAmountInNetworkEstimate)}}</td>
                                <td class="text-center">
                                    <!--can only Edit/Delete when status is Proposed-->
                                    <em @click="EditEstimate(item.estimationId ?? 0)" class="fa fa-pencil me-1 ps-2" style="cursor: pointer;" title="Edit/View Estimate"></em>
                                    <em @click="PrintableView(item.estimationId ?? 0, item.isEstimateAccepted, false)" class="fa fa-print me-1 ps-1" style="cursor: pointer;" title="Print Estimate"></em>
                                    <em v-if="!item.isEstimateAccepted" @click="DeleteEstimate(item.estimationId ?? 0, false, true)" class="fa fa-close me-1 ps-1" style="color:red; cursor: pointer;" title="Delete Proposed Estimate"></em>
                                    <em v-if="item.patientElectronicSignatureDate" @click="GetSignatureInfo(item.patientElectronicSignatureDate, item.estimationId)" class="fas fa-file-signature me-1 ps-1" style="cursor: pointer;" :title="`Patient Electronically Signed Estimate on ${DateUtils.toDisplayDateString(item.patientElectronicSignatureDate, DateDisplayFormat.NumericDate)} ${DateUtils.prettyTime(item.patientElectronicSignatureDate)}`"></em>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>                
            </div>

            <template v-slot:footer>
                <button @click="closeEstimateHistoryDialog" type="button" class="btn btn-primary">Close</button>
            </template>
        </ModalDialog>

        <!--Modal Dialog to Display Patient Electronic Signature-->
        <ModalDialog 
            v-model:show="formData.showPatientSignatureModal"
            :size="ModalSize.Medium"
        >
            <template v-slot:title>Estimate Signature</template>

            <div class="row">
                <div class="col">
                    {{ formData.patientElectronicSignatureText }}
                </div>
            </div>
            <template v-slot:footer>
                <ChoiceButton 
                        :size="ButtonDisplaySize.Small"
                        :action="ActionButtonStyle.Close" 
                        text=" Close" 
                        title="Close Signature Window" 
                        @click="closePatientSignatureModal" />
            </template>
        </ModalDialog>

        
        <table v-if="hasPatientPayers" class="table table-bordered table-responsive mt-3" aria-describedby="Estimation Lines table">
            <thead class="bg-light border-isalus-primary text-isalus-primary">
                <tr>
                    <th scope="col">Date of Service</th>
                    <th scope="col">Procedure</th>
                    <th scope="col">Diagnosis</th>
                    <th scope="col">Modifiers</th>
                    <th scope="col" class="text-end">Units</th>
                    <th scope="col" class="text-end">Billed per Unit</th>
                    <th scope="col" class="text-end">Allowed per Unit</th>
                    <th scope="col" class="text-end">Billed Total</th>
                    <th scope="col" class="text-end">Allowed Total</th>
                    <th v-if="formData.estimationLinesResponseData.filter(item => item.isEstimateAccepted == false).length > 0" scope="col" class="text-center">Action</th>
                </tr>
            </thead>
            <tbody class="border-isalus-primary">
                <tr v-for="line in formData.estimationLinesResponseData">
                    <td>{{DateUtils.toDisplayDateString(line.dateOfService, DateDisplayFormat.NumericDate)}}</td>
                    <td>{{line.procedureCode}} {{line.procedureDesc}}</td>
                    <td>{{line.diagnosisCode}} {{ line.diagnosisDesc }}</td>
                    <td>{{line.modifier}}</td>
                    <td class="text-end">{{line.units}}</td>
                    <td class="text-end" :class="{'bg-danger': (0 === line.chargeAmount)}">{{ToMoneyStr(line.chargeAmount)}}</td>
                    <td class="text-end" :class="{'bg-danger': (0 === line.allowedAmount)}">{{ToMoneyStr(line.allowedAmount)}}</td>
                    <td class="text-end" :class="{'bg-danger': (0 === calcExtendedAmount(line.units, line.chargeAmount))}">{{ToMoneyStr(calcExtendedAmount(line.units, line.chargeAmount))}}</td>
                    <td class="text-end" :class="{'bg-danger': (0 === calcExtendedAmount(line.units, line.allowedAmount))}">{{ToMoneyStr(calcExtendedAmount(line.units, line.allowedAmount))}}</td>
                    <td v-if="formData.estimationLinesResponseData.filter(item => item.isEstimateAccepted == false).length > 0" class="text-center">
                        <!--cannot delete line if it originated from an appt type-->
                        <em @click="DeleteLine(line.estimationLineId, line.estimationId, line.isManualEntry)" class="fa fa-close me-3" style="color:red; cursor: pointer;" title="Delete Line"></em>
                        <em @click="EditLine(line)" class="fa fa-pencil me-1" style="cursor: pointer;" title="Edit Line"></em>
                    </td>
                </tr>
                <tr class="fw-bold">
                    <td colspan="7" class="text-end pe-3">
                        Total
                    </td>
                    <td class="text-end">
                        {{ToMoneyStr(totalChargeAmountExtended)}}
                    </td>
                    <td class="text-end">
                        {{ToMoneyStr(totalAllowedAmountExtended)}}
                    </td>
                </tr>
            </tbody>
        </table>
        </div>

        <div class="row ps-2 pt-2">
            <div class="col">
                <div class="btn-toolbar">
                    <!-- Do not show Appt Type button if the client does not have any appt types mapped to CPTs -->
                    <button v-if="formData.apptTypeCPTCount > 0 && showAddLineButtons && !formData.estimateInfo?.isEstimateAccepted" type="button" class="btn btn-outline-primary btn-sm me-3" @click="AddLineByApptType">Add Line By Appt Type</button>
                    <button v-if="showAddLineButtons && !formData.estimateInfo?.isEstimateAccepted" type="button" class="btn btn-outline-primary btn-sm" @click="AddLineByManualEntry">Add Line By Manual Entry</button>
                </div>                
            </div>
        </div>


    <!--Patient Benefits Section-->
    <div v-if="hasPatientPayers" class="container-fluid mt-4">        
        <div class="row">
            <div class="col fw-bold text-isalus-primary fs-5 pt-2 mb-2">
                Patient Benefits
            </div>
        </div>
        <TitledRow title="Insurance Plan" content-class="col-sm-10 mb-2" :title-width="2">
            <div class="col-md-10">
                <span class="pe-3">{{formData.selectedPayer?.payerName}} - {{formData.selectedPayer?.insuredIdCode}}</span>
                <span class="pe-2"><button type="button" v-if="!isSelfPay" class="btn btn-outline-primary btn-sm" @click="CheckEligibility">Check Eligibility</button></span>
                <span class="pe-2"><button type="button" v-if="formData.estimateInfo && !isSelfPay && !formData.estimateInfo.isEstimateAccepted" :class="copayButtonClass" :title="copayTitleClass" @click="BenefitsManualEntry">Manual Entry</button></span>
                <span v-if="(hasMultipleCopayInNetwork || hasMultipleCopayOutOfNetwork) && !formData.estimateInfo?.isEstimateAccepted" :class="copayClass">Warning: Multiple copays returned</span>                
            </div>
        </TitledRow>
        <TitledRow v-if="!isSelfPay" title="Network" content-class="col-md-10 mb-2" :title-width="2">
            <div class="col-md-3 btn-group" role="group" aria-label="Toggle Network">
                <input type="radio" v-model="formData.selectedNetwork" @click="NetworkOptions(NetworkTypes.In)" class="btn-check" id="btnNetworkIn" value="in" checked>
                <label class="btn btn-outline-primary btn-sm" for="btnNetworkIn">In Network</label>

                <input type="radio" v-model="formData.selectedNetwork" @click="NetworkOptions(NetworkTypes.Out)" class="btn-check" id="btnNetworkOut" value="out">
                <label class="btn btn-outline-primary btn-sm" for="btnNetworkOut">Out of Network</label>
            </div>
        </TitledRow>
        <TitledRow v-if="!isSelfPay" title="Coverage Category" content-class="col-md-3 mb-2" :title-width="2">
            <select class="form-select" v-model="formData.selectedServiceType" required="true" @change="Load271Response()">  
                <option v-for="servicetype in formData.serviceTypeCodeList" :value="servicetype">                                
                    {{servicetype.serviceTypeDesc}} ({{servicetype.serviceTypeCode}})
                </option>
            </select>
        </TitledRow> 
        <TitledRow v-if="multipleCoverageCategoriesUsedMessage" title="" content-class="col-md-3 mb-2" :title-width="2">
            <span class="text-danger">{{ multipleCoverageCategoriesUsedMessage }}</span>
        </TitledRow>  
        <TitledRow v-if="!isSelfPay" title="Last Eligibility Check" content-class="col-md-3 mb-2" :title-width="2">
            {{DateUtils.toDisplayDateString(formData.lastEligCheck?.lastEligibilityDate, DateDisplayFormat.NumericDate)}}
            <div v-if="EligibilityCoverage.Inactive == formData.lastEligCheck?.overallCoverage" class="text-danger">Insurance is inactive, so 100% patient cost</div>
        </TitledRow>        
        <div v-cloak class="row" v-if="(needsNewEligCheck && !formData.savedManualEntry && !isSelfPay)">
            <TitledRow title="" content-class="col-md-6 mb-4 text-danger" :title-width="2">
                {{ formData.messageManualEntry }}
            </TitledRow>
        </div>

        <div class="row" v-if="isSelfPay">
            <div class="col-md-6 mb-4 text-danger">The insured is Self Pay and will be responsible for 100% of the cost</div>
        </div>

        <div class="row mt-3" v-if="(formData.estimateInfo?.estimationId ?? 0) > 0 && !formData.estimateInfo?.isEstimateAccepted && (!needsNewEligCheck || formData.savedManualEntry || isSelfPay) && ( (formData.estimateInfo?.allowedAmount ?? 0 > 0) || (formData.estimateInfo?.chargeAmount ?? 0 > 0) )">
            <div class="col-md-2 mb-2">   
                <div class="d-grid gap-2 mb-4">
                    <button type="button" class="btn btn-primary btn-block" title="Calculate the Estimate using given inputs" @click="CalculateEstimate(false)">Calculate</button>
                </div>            
            </div>
            <div class="col-md-2 mb-2" v-if="formData.showSummary">
                <div class="d-grid gap-2 mb-4">
                    <button type="button" class="btn btn-primary btn-block" title="Accept this Estimate and Lock it from further editing" @click="AcceptEstimate">Accept</button>
                </div>            
            </div>
        </div>

        <div class="row mt-2 ps-2 text-success" v-if="(formData.showSummary && formData.estimateInfo?.isEstimateAccepted)">
            This estimate has been accepted and locked.
        </div>
    </div>

    <div v-if="formData.showSummary">
        <hr>
        <!--Summary and Calculation Section-->
        <div class="container-fluid mt-3 mb-5">        
            <div class="row">
                <div class="col fw-bold text-isalus-primary fs-5 pt-2 mb-2">
                    Summary <em @click="PrintableView(formData.estimateInfo?.estimationId ?? 0, formData.estimateInfo?.isEstimateAccepted ?? false, true)" class="fa fa-print ps-2" style="cursor: pointer;" title="Print Estimate"></em>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3 container-isalus-primary"></div>
                <div class="col-md-3 container-isalus-primary text-isalus-primary fw-bold text-end">
                    Patient
                </div>
                <div class="col-md-3 container-isalus-primary text-isalus-primary fw-bold text-end">
                    Insurance
                </div>
                <div class="col-md-1" style="width: 20px;"></div>
                <div class="col-md-2 background-isalus-primary"></div>
            </div>
            <div class="row">
                <div class="col-md-3 container-isalus-primary text-isalus-primary">
                    <div class="row">
                        <div class="col-md-6 fw-bold" :class="copayClass" :title="copayTitleClass" :style="copayStyle" @click="BenefitsManualEntry">
                            Copay
                        </div>
                        <div class="col-md-6 text-end">
                            {{ToMoneyStr(summaryPatientCoPay)}}
                        </div>
                    </div>
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    {{ToMoneyStr(calcPatientCoPay)}}
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    {{ToMoneyStr(totalEstAllowedOrCharge)}} - {{ToMoneyStr(calcPatientCoPay)}} = {{ToMoneyStr(calcInsuranceCoPay)}}
                </div>
                <div class="col-md-1" style="width: 20px;"></div>
                <div class="col-md-2 background-isalus-primary fw-bold text-center">
                    Insurance Coverage
                </div>
            </div>
            <div class="row">
                <div class="col-md-3 container-isalus-primary text-isalus-primary">
                    Deductible
                    <div class="row show-grid">
                        <div class="col-md-6 fw-bold ps-4">
                            Individual
                        </div>
                        <div class="col-md-6 text-end">
                            {{ToMoneyStr(summaryDeductRemainingInd)}} / {{ToMoneyStr(summaryDeductInd)}}
                        </div>
                    </div>
                    <div class="row show-grid">
                        <div class="col-md-6 fw-bold ps-4">
                            Family
                        </div>
                        <div class="col-md-6 text-end">
                            {{ToMoneyStr(summaryDeductRemainingFam)}} / {{ToMoneyStr(summaryDeductFam)}}
                        </div>
                    </div>
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    <br>{{ToMoneyStr(calcPatientDeduct)}}
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    <br>{{ToMoneyStr(calcInsuranceCoPay)}} - {{ToMoneyStr(calcPatientDeduct)}} = {{ToMoneyStr(calcInsuranceDeduct)}}
                </div>
                <div class="col-md-1" style="width: 20px;"></div>
                <div class="col-md-2 background-isalus-primary fw-bold text-center">
                    {{ToMoneyStr(calcTotalInsurance)}}
                </div>
            </div>
            <div class="row">
                <div class="col-md-3 container-isalus-primary text-isalus-primary">
                    <div class="row">
                        <div class="col-md-6 fw-bold">
                            Co-insurance
                        </div>
                        <div class="col-md-6 text-end">
                            {{ToPercentStr(summaryCoIns)}}
                        </div>
                    </div>
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    {{ToMoneyStr(calcPatientCoInsurance)}}
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    {{ToMoneyStr(calcInsuranceDeduct)}} - {{ToMoneyStr(calcPatientCoInsurance)}} = {{ToMoneyStr(calcInsuranceCoInsurance)}}
                </div>
                <div class="col-md-1" style="width: 20px;"></div>
                <div class="col-md-2 background-isalus-primary fw-bold text-center">
                    Patient Cost
                </div>
            </div>
            <div class="row">
                <div class="col-md-3 container-isalus-primary fw-bold">
                    Total for Now
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    {{ToMoneyStr(calcTotalPatient)}}
                </div>
                <div class="col-md-3 fw-bold text-end border-isalus-primary">
                    {{ToMoneyStr(calcTotalInsurance)}}
                </div>
                <div class="col-md-1" style="width: 20px;"></div>
                <div class="col-md-2 background-isalus-primary fw-bold text-center">
                    {{ToMoneyStr(calcTotalPatient)}}
                </div>
            </div>
        </div>
    </div>

    <div id="ReportHTML" style="display:none"></div>

    <ChoiceStateMessageSink :channel-name="MessageChannelName.PatientCostEstimator" @message="sinkMessageForMmlSave"></ChoiceStateMessageSink>
    <ChoiceStateMessageSink :channel-name="MessageChannelName.PatientEligibility" @message="sinkMessageForEligibilityCheck"></ChoiceStateMessageSink>

</template>


